import { todayMx } from '../lib/dateUtils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ArrowDown, ArrowUp, Wallet, KeyRound, X, Search, Ban, Lock, Users as UsersIcon, Edit } from 'lucide-react';

const MOTIVOS = [
  { value: 'prestamo', label: 'Préstamo' },
  { value: 'compra_material', label: 'Compra de material' },
  { value: 'gasolina', label: 'Gasolina' },
  { value: 'herramienta', label: 'Herramienta' },
  { value: 'anticipo', label: 'Anticipo' },
  { value: 'otro', label: 'Otro' },
];
const motivoLabel = (m) => MOTIVOS.find(x => x.value === m)?.label || m || '-';

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Caja() {
  const { user } = useAuth();
  const [saldo, setSaldo] = useState({ saldo: 0, entradas: 0, salidas: 0, canceladas: 0, can_manage: false });
  const [movs, setMovs] = useState([]);
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterPersona, setFilterPersona] = useState('');
  const [hasPin, setHasPin] = useState(true);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinForm, setPinForm] = useState({ pin: '', pin2: '' });
  const [techPinDialogOpen, setTechPinDialogOpen] = useState(false);
  const [techPinTarget, setTechPinTarget] = useState(null);
  const [techPinForm, setTechPinForm] = useState({ pin: '', pin2: '' });
  const [entradaOpen, setEntradaOpen] = useState(false);
  const [salidaOpen, setSalidaOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileTarget, setReconcileTarget] = useState(null);
  const [reconcileForm, setReconcileForm] = useState({ monto_gastado: 0, monto_devuelto: 0, requiere_ticket: false, ticket_entregado: false, observaciones: '' });
  const [saving, setSaving] = useState(false);

  const [entradaForm, setEntradaForm] = useState({ fecha: todayMx(), cantidad: '', concepto: '', observaciones: '' });
  const [salidaForm, setSalidaForm] = useState({ fecha: todayMx(), cantidad: '', motivo: 'compra_material', concepto: '', entregado_por_user_id: '', recibido_por_user_id: '', pin_entrega: '', pin_recibe: '', observaciones: '', requiere_ticket: false, ticket_entregado: false });

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterTipo !== 'all') params.append('tipo', filterTipo);
      if (filterFrom) params.append('fecha_from', filterFrom);
      if (filterTo) params.append('fecha_to', filterTo);
      if (filterPersona) params.append('persona', filterPersona);
      const [s, m, u, t, p] = await Promise.all([
        axios.get(`${API}/caja/saldo`),
        axios.get(`${API}/caja/movimientos?${params}`),
        axios.get(`${API}/caja/users`),
        axios.get(`${API}/caja/technicians`),
        axios.get(`${API}/users/me/pin-status`),
      ]);
      setSaldo(s.data);
      setMovs(m.data);
      setUsers(u.data);
      setTechnicians(t.data);
      setHasPin(p.data.has_pin);
    } catch { toast.error('Error al cargar Caja'); }
    finally { setLoading(false); }
  }, [filterTipo, filterFrom, filterTo, filterPersona]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [fetchData]);

  const canManage = saldo.can_manage;
  const isAdmin = user?.role === 'admin';

  const handleSetMyPin = async (e) => {
    e.preventDefault();
    if (!pinForm.pin || pinForm.pin.length < 4) { toast.error('La clave debe tener al menos 4 caracteres'); return; }
    if (pinForm.pin !== pinForm.pin2) { toast.error('Las claves no coinciden'); return; }
    try {
      await axios.post(`${API}/users/me/pin`, { pin: pinForm.pin });
      toast.success('Clave de caja guardada');
      setPinDialogOpen(false);
      setPinForm({ pin: '', pin2: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const openTechPin = (tech) => {
    setTechPinTarget(tech);
    setTechPinForm({ pin: '', pin2: '' });
  };

  const handleSetTechPin = async (e) => {
    e.preventDefault();
    if (!techPinTarget) return;
    if (!techPinForm.pin || techPinForm.pin.length < 4) { toast.error('La clave debe tener al menos 4 caracteres'); return; }
    if (techPinForm.pin !== techPinForm.pin2) { toast.error('Las claves no coinciden'); return; }
    try {
      await axios.post(`${API}/technicians/${techPinTarget.id}/caja-pin`, { pin: techPinForm.pin });
      toast.success(`Clave de ${techPinTarget.name} guardada`);
      setTechPinTarget(null);
      setTechPinForm({ pin: '', pin2: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleEntrada = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/caja/entrada`, entradaForm);
      toast.success('Entrada registrada');
      setEntradaOpen(false);
      setEntradaForm({ fecha: todayMx(), cantidad: '', concepto: '', observaciones: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleSalida = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/caja/salida`, salidaForm);
      toast.success('Salida registrada');
      setSalidaOpen(false);
      setSalidaForm({ fecha: todayMx(), cantidad: '', motivo: 'compra_material', concepto: '', entregado_por_user_id: '', recibido_por_user_id: '', pin_entrega: '', pin_recibe: '', observaciones: '', requiere_ticket: false, ticket_entregado: false });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const openCancel = (m) => { setCancelTarget(m); setCancelMotivo(''); setCancelOpen(true); };

  const openReconcile = (m) => {
    setReconcileTarget(m);
    setReconcileForm({
      monto_gastado: m.monto_gastado || 0,
      monto_devuelto: m.monto_devuelto || 0,
      requiere_ticket: m.requiere_ticket || false,
      ticket_entregado: m.ticket_entregado || false,
      observaciones: m.reconciliacion_observaciones || '',
    });
    setReconcileOpen(true);
  };

  const handleReconcile = async (e) => {
    e.preventDefault();
    if (!reconcileTarget) return;
    try {
      await axios.post(`${API}/caja/movimientos/${reconcileTarget.id}/reconciliar`, reconcileForm);
      toast.success('Movimiento reconciliado');
      setReconcileOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await axios.post(`${API}/caja/movimientos/${cancelTarget.id}/cancelar`, { motivo: cancelMotivo });
      toast.success('Movimiento cancelado');
      setCancelOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const movStatus = (m) => {
    if (m.status === 'cancelado') return null;
    if (m.tipo !== 'salida') return null;
    const reconciliado = !!m.reconciliado;
    const pend = parseFloat(m.pendiente_devolver || 0) || 0;
    const devuelto = parseFloat(m.monto_devuelto || 0) || 0;
    if (reconciliado) return { cls: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200', label: '✅ Cerrado' };
    if (devuelto > 0 && pend > 0) return { cls: 'bg-amber-200 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200', label: `Pendiente devolver $${pend.toLocaleString('es-MX',{minimumFractionDigits:2})}` };
    if (m.requiere_ticket && !m.ticket_entregado) return { cls: 'bg-orange-200 text-orange-900 dark:bg-orange-900/50 dark:text-orange-200', label: '🎫 Ticket pendiente' };
    if (pend > 0) return { cls: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300', label: 'Pendiente reconciliar' };
    return { cls: 'bg-blue-200 text-blue-900', label: 'Reconciliar' };
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="caja-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><Wallet className="h-6 w-6 text-emerald-600" />Caja REPACS</h2>
          <p className="text-muted-foreground text-sm">Control de efectivo: entradas, salidas y movimientos internos</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setPinDialogOpen(true)} data-testid="caja-set-pin"><KeyRound className="h-4 w-4 mr-1" />{hasPin ? 'Cambiar mi clave' : 'Configurar mi clave'}</Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setTechPinDialogOpen(true)} data-testid="caja-tech-pins-btn"><UsersIcon className="h-4 w-4 mr-1" />Claves técnicos</Button>
          )}
          {canManage && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setEntradaOpen(true)} data-testid="caja-nueva-entrada"><ArrowDown className="h-4 w-4 mr-1" />Nueva entrada</Button>
          )}
          <Button size="sm" variant="destructive" onClick={() => setSalidaOpen(true)} data-testid="caja-nueva-salida"><ArrowUp className="h-4 w-4 mr-1" />Nueva salida</Button>
        </div>
      </div>

      {/* Saldo banner */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-700/10 border-emerald-500/40">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold">Saldo actual</p>
              <p className="text-4xl font-bold mt-1" data-testid="caja-saldo">{fmt(saldo.saldo)}</p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="text-xs text-muted-foreground">Entradas</p><p className="font-bold text-emerald-600">{saldo.entradas}</p></div>
              <div><p className="text-xs text-muted-foreground">Salidas</p><p className="font-bold text-red-600">{saldo.salidas}</p></div>
              <div><p className="text-xs text-muted-foreground">Canceladas</p><p className="font-bold text-muted-foreground">{saldo.canceladas}</p></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card><CardContent className="p-3 flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs">Tipo</Label>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="entrada">Entradas</SelectItem>
              <SelectItem value="salida">Salidas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Desde</Label><Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="w-[140px]" /></div>
        <div className="space-y-1"><Label className="text-xs">Hasta</Label><Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="w-[140px]" /></div>
        <div className="space-y-1 flex-1 min-w-[180px]">
          <Label className="text-xs">Persona</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Nombre..." value={filterPersona} onChange={(e) => setFilterPersona(e.target.value)} className="pl-8" />
          </div>
        </div>
        {(filterTipo !== 'all' || filterFrom || filterTo || filterPersona) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterTipo('all'); setFilterFrom(''); setFilterTo(''); setFilterPersona(''); }}><X className="h-4 w-4 mr-1" />Limpiar</Button>
        )}
      </CardContent></Card>

      {/* Movements table */}
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="caja-table">
          <thead className="bg-muted/60">
            <tr className="text-xs">
              <th className="text-left px-2 py-2">Folio</th>
              <th className="text-left px-2 py-2">Fecha</th>
              <th className="text-left px-2 py-2">Tipo</th>
              <th className="text-left px-2 py-2">Concepto / Motivo</th>
              <th className="text-right px-2 py-2">Cantidad</th>
              <th className="text-right px-2 py-2">Saldo</th>
              <th className="text-left px-2 py-2">Entregó</th>
              <th className="text-left px-2 py-2">Recibió</th>
              <th className="text-left px-2 py-2">Registrado por</th>
              <th className="text-left px-2 py-2">Obs.</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="text-center py-8 text-muted-foreground">Cargando...</td></tr>
            ) : movs.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-8 text-muted-foreground">Sin movimientos</td></tr>
            ) : movs.map(m => {
              const cancelled = m.status === 'cancelado';
              const isEntrada = m.tipo === 'entrada';
              return (
                <tr key={m.id} className={`border-t ${cancelled ? 'opacity-50 line-through' : isEntrada ? 'bg-emerald-50/40 dark:bg-emerald-950/20' : 'bg-red-50/40 dark:bg-red-950/20'}`}>
                  <td className="px-2 py-1.5 font-mono text-xs">{m.folio}</td>
                  <td className="px-2 py-1.5 text-xs">{m.fecha}</td>
                  <td className="px-2 py-1.5">
                    {cancelled ? <Badge variant="outline" className="text-xs">Cancelado</Badge> :
                      isEntrada ? <Badge className="bg-emerald-600 text-xs">Entrada</Badge> :
                                  <Badge className="bg-red-600 text-xs">Salida</Badge>}
                  </td>
                  <td className="px-2 py-1.5 text-xs">
                    <p>{m.concepto || '-'}</p>
                    {m.motivo && <p className="text-muted-foreground">{motivoLabel(m.motivo)}</p>}
                  </td>
                  <td className={`px-2 py-1.5 text-right font-semibold ${isEntrada ? 'text-emerald-600' : 'text-red-600'}`}>{isEntrada ? '+' : '-'}{fmt(m.cantidad)}</td>
                  <td className="px-2 py-1.5 text-right text-xs font-mono">{fmt(m.saldo_despues)}</td>
                  <td className="px-2 py-1.5 text-xs">{m.entregado_por_name || '-'}{m.confirmado_con_clave && <Lock className="inline h-3 w-3 ml-1 text-emerald-600" title="Confirmado con clave" />}</td>
                  <td className="px-2 py-1.5 text-xs">{m.recibido_por_name || '-'}</td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">{m.registrado_por}</td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground max-w-[160px] truncate" title={m.observaciones}>{m.observaciones || ''}{cancelled && m.cancellation_reason && <span className="block text-red-500">⛔ {m.cancellation_reason}</span>}{(() => { const st = movStatus(m); return st ? <span className={`block mt-1 text-[10px] px-1.5 py-0.5 rounded ${st.cls}`}>{st.label}</span> : null; })()}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1">
                      {m.tipo === 'salida' && !cancelled && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" title="Reconciliar (ticket, gastado, devuelto)" onClick={() => openReconcile(m)} data-testid={`caja-reconcile-${m.id}`}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isAdmin && !cancelled && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Cancelar movimiento" onClick={() => openCancel(m)} data-testid={`caja-cancel-${m.id}`}>
                          <Ban className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent></Card>

      {/* Set PIN dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{hasPin ? 'Cambiar mi clave' : 'Configurar mi clave de caja'}</DialogTitle>
            <DialogDescription>Esta clave se usará para confirmar movimientos de dinero. Mínimo 4 caracteres.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSetMyPin} className="space-y-3">
            <div><Label className="text-xs">Nueva clave</Label><Input type="password" autoComplete="new-password" value={pinForm.pin} onChange={(e) => setPinForm({...pinForm, pin: e.target.value})} required minLength={4} data-testid="caja-pin1" /></div>
            <div><Label className="text-xs">Confirmar clave</Label><Input type="password" autoComplete="new-password" value={pinForm.pin2} onChange={(e) => setPinForm({...pinForm, pin2: e.target.value})} required minLength={4} data-testid="caja-pin2" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setPinDialogOpen(false)}>Cancelar</Button><Button type="submit" data-testid="caja-pin-save">Guardar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Entrada dialog */}
      <Dialog open={entradaOpen} onOpenChange={setEntradaOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowDown className="h-5 w-5 text-emerald-600" />Nueva entrada de dinero</DialogTitle>
            <DialogDescription>El monto se sumará al saldo de caja.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEntrada} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Fecha</Label><Input type="date" value={entradaForm.fecha} onChange={(e) => setEntradaForm({...entradaForm, fecha: e.target.value})} /></div>
              <div><Label className="text-xs">Cantidad *</Label><Input type="number" min="0.01" step="0.01" value={entradaForm.cantidad} onChange={(e) => setEntradaForm({...entradaForm, cantidad: e.target.value})} required data-testid="caja-entrada-cantidad" /></div>
            </div>
            <div><Label className="text-xs">Concepto / motivo *</Label><Input value={entradaForm.concepto} onChange={(e) => setEntradaForm({...entradaForm, concepto: e.target.value})} required placeholder="Ej: Cobro servicio Juan Pérez" data-testid="caja-entrada-concepto" /></div>
            <div><Label className="text-xs">Observaciones</Label><Textarea value={entradaForm.observaciones} onChange={(e) => setEntradaForm({...entradaForm, observaciones: e.target.value})} rows={2} /></div>
            <p className="text-xs text-muted-foreground">Registrado por: <span className="font-semibold">{user?.full_name}</span></p>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setEntradaOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="caja-entrada-submit">Guardar entrada</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Salida dialog */}
      <Dialog open={salidaOpen} onOpenChange={setSalidaOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowUp className="h-5 w-5 text-red-600" />Nueva salida de dinero</DialogTitle>
            <DialogDescription>Saldo disponible: <span className="font-bold text-emerald-600">{fmt(saldo.saldo)}</span></DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSalida} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Fecha</Label><Input type="date" value={salidaForm.fecha} onChange={(e) => setSalidaForm({...salidaForm, fecha: e.target.value})} /></div>
              <div><Label className="text-xs">Cantidad *</Label><Input type="number" min="0.01" step="0.01" value={salidaForm.cantidad} onChange={(e) => setSalidaForm({...salidaForm, cantidad: e.target.value})} required data-testid="caja-salida-cantidad" /></div>
            </div>
            <div><Label className="text-xs">Motivo *</Label>
              <Select value={salidaForm.motivo} onValueChange={(v) => setSalidaForm({...salidaForm, motivo: v})}>
                <SelectTrigger data-testid="caja-salida-motivo"><SelectValue /></SelectTrigger>
                <SelectContent>{MOTIVOS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Concepto / detalle</Label><Input value={salidaForm.concepto} onChange={(e) => setSalidaForm({...salidaForm, concepto: e.target.value})} placeholder="Ej: Pago de tubo CPVC" /></div>

            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <div>
                <Label className="text-xs">¿Se requiere ticket? *</Label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" size="sm" variant={salidaForm.requiere_ticket === true ? 'default' : 'outline'} onClick={() => setSalidaForm({...salidaForm, requiere_ticket: true})} data-testid="caja-ticket-si">Sí</Button>
                  <Button type="button" size="sm" variant={salidaForm.requiere_ticket === false ? 'default' : 'outline'} onClick={() => setSalidaForm({...salidaForm, requiere_ticket: false, ticket_entregado: false})} data-testid="caja-ticket-no">No</Button>
                </div>
              </div>
              {salidaForm.requiere_ticket && (
                <div>
                  <Label className="text-xs">Ticket entregado</Label>
                  <div className="flex gap-2 mt-1">
                    <Button type="button" size="sm" variant={salidaForm.ticket_entregado === true ? 'default' : 'outline'} className={salidaForm.ticket_entregado === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => setSalidaForm({...salidaForm, ticket_entregado: true})} data-testid="caja-ticket-entregado-si">✅ Sí</Button>
                    <Button type="button" size="sm" variant={salidaForm.ticket_entregado === false ? 'default' : 'outline'} className={salidaForm.ticket_entregado === false ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setSalidaForm({...salidaForm, ticket_entregado: false})} data-testid="caja-ticket-entregado-no">❌ No</Button>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-md border bg-amber-50/40 dark:bg-amber-950/20 p-3 space-y-3">
              <p className="text-xs font-semibold flex items-center gap-1"><Lock className="h-3 w-3" />Confirmación con clave personal</p>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <Label className="text-xs">Quien entrega *</Label>
                  <select className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm" value={salidaForm.entregado_por_user_id} onChange={(e) => setSalidaForm({...salidaForm, entregado_por_user_id: e.target.value})} required data-testid="caja-entrega-user">
                    <option value="">Seleccionar...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>)}
                  </select>
                  <Input type="password" autoComplete="off" placeholder="Clave de quien entrega" value={salidaForm.pin_entrega} onChange={(e) => setSalidaForm({...salidaForm, pin_entrega: e.target.value})} required className="mt-1" data-testid="caja-pin-entrega" />
                </div>
                <div>
                  <Label className="text-xs">Quien recibe *</Label>
                  <select className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm" value={salidaForm.recibido_por_user_id} onChange={(e) => setSalidaForm({...salidaForm, recibido_por_user_id: e.target.value})} required data-testid="caja-recibe-user">
                    <option value="">Seleccionar...</option>
                    {technicians.length > 0 && (
                      <optgroup label="Técnicos y ayudantes">
                        {technicians.map(t => <option key={`t-${t.id}`} value={t.id} disabled={!t.has_pin}>{t.name} {t.tipo === 'ayudante' ? '(Ayudante)' : ''}{!t.has_pin ? ' — sin clave' : ''}</option>)}
                      </optgroup>
                    )}
                    {users.length > 0 && (
                      <optgroup label="Usuarios del sistema">
                        {users.map(u => <option key={`u-${u.id}`} value={u.id}>{u.full_name} ({u.username})</option>)}
                      </optgroup>
                    )}
                  </select>
                  <Input type="password" autoComplete="off" placeholder="Clave de quien recibe" value={salidaForm.pin_recibe} onChange={(e) => setSalidaForm({...salidaForm, pin_recibe: e.target.value})} required className="mt-1" data-testid="caja-pin-recibe" />
                </div>
              </div>
            </div>

            <div><Label className="text-xs">Observaciones</Label><Textarea value={salidaForm.observaciones} onChange={(e) => setSalidaForm({...salidaForm, observaciones: e.target.value})} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setSalidaOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving} variant="destructive" data-testid="caja-salida-submit">Guardar salida</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cancelar movimiento</DialogTitle>
            <DialogDescription>El movimiento {cancelTarget?.folio} ({fmt(cancelTarget?.cantidad)}) quedará marcado como cancelado y su efecto se revierte del saldo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Motivo de cancelación</Label>
            <Textarea value={cancelMotivo} onChange={(e) => setCancelMotivo(e.target.value)} rows={2} placeholder="Ej: error de captura" />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCancelOpen(false)}>Volver</Button><Button variant="destructive" onClick={handleCancel} data-testid="caja-cancel-confirm">Cancelar movimiento</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reconcile dialog (ticket + gastado + devuelto) */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reconciliar salida {reconcileTarget?.folio}</DialogTitle>
            <DialogDescription>
              Monto entregado: <span className="font-bold text-red-600">{fmt(reconcileTarget?.cantidad)}</span> · a <span className="font-medium">{reconcileTarget?.recibido_por_name}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReconcile} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Monto comprobado con ticket</Label>
                <Input type="number" min="0" step="0.01" value={reconcileForm.monto_gastado} onChange={(e) => setReconcileForm({...reconcileForm, monto_gastado: e.target.value})} data-testid="caja-rec-gastado" />
              </div>
              <div>
                <Label className="text-xs">Monto devuelto a caja</Label>
                <Input type="number" min="0" step="0.01" value={reconcileForm.monto_devuelto} onChange={(e) => setReconcileForm({...reconcileForm, monto_devuelto: e.target.value})} data-testid="caja-rec-devuelto" />
              </div>
            </div>

            {(() => {
              const total = parseFloat(reconcileTarget?.cantidad || 0) || 0;
              const g = parseFloat(reconcileForm.monto_gastado) || 0;
              const d = parseFloat(reconcileForm.monto_devuelto) || 0;
              const pend = +(total - g - d).toFixed(2);
              const exceed = (g + d) > total + 0.001;
              return (
                <div className={`rounded-md border p-3 text-sm space-y-1 ${exceed ? 'border-red-500 bg-red-50/40 dark:bg-red-950/20' : pend <= 0 ? 'border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20' : 'bg-amber-50/40 dark:bg-amber-950/20'}`}>
                  <div className="flex justify-between"><span className="text-muted-foreground">Entregado</span><span className="font-medium">{fmt(total)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Gastado (con ticket)</span><span className="font-medium text-red-600">-{fmt(g)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Devuelto a caja</span><span className="font-medium text-emerald-600">-{fmt(d)}</span></div>
                  <div className="flex justify-between border-t pt-1"><span className="font-semibold">{exceed ? '⚠️ Excede el entregado' : pend <= 0 ? '✅ Movimiento cerrado' : 'Pendiente por devolver'}</span><span className={`font-bold ${exceed ? 'text-red-600' : pend <= 0 ? 'text-emerald-600' : 'text-amber-700'}`}>{fmt(pend)}</span></div>
                </div>
              );
            })()}

            <div className="rounded-md border bg-muted/30 p-3 space-y-2">
              <div>
                <Label className="text-xs">¿Se requiere ticket?</Label>
                <div className="flex gap-2 mt-1">
                  <Button type="button" size="sm" variant={reconcileForm.requiere_ticket === true ? 'default' : 'outline'} onClick={() => setReconcileForm({...reconcileForm, requiere_ticket: true})}>Sí</Button>
                  <Button type="button" size="sm" variant={reconcileForm.requiere_ticket === false ? 'default' : 'outline'} onClick={() => setReconcileForm({...reconcileForm, requiere_ticket: false, ticket_entregado: false})}>No</Button>
                </div>
              </div>
              {reconcileForm.requiere_ticket && (
                <div>
                  <Label className="text-xs">Ticket entregado</Label>
                  <div className="flex gap-2 mt-1">
                    <Button type="button" size="sm" variant={reconcileForm.ticket_entregado === true ? 'default' : 'outline'} className={reconcileForm.ticket_entregado === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => setReconcileForm({...reconcileForm, ticket_entregado: true})}>✅ Sí</Button>
                    <Button type="button" size="sm" variant={reconcileForm.ticket_entregado === false ? 'default' : 'outline'} className={reconcileForm.ticket_entregado === false ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setReconcileForm({...reconcileForm, ticket_entregado: false})}>❌ No</Button>
                  </div>
                </div>
              )}
            </div>

            <div><Label className="text-xs">Observaciones</Label><Textarea value={reconcileForm.observaciones} onChange={(e) => setReconcileForm({...reconcileForm, observaciones: e.target.value})} rows={2} placeholder="Ej: Ticket de Home Depot, sobrante regresado el martes" /></div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReconcileOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="caja-rec-submit">Guardar reconciliación</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tech PINs dialog (admin/emma) */}
      <Dialog open={techPinDialogOpen} onOpenChange={setTechPinDialogOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Claves de técnicos y ayudantes</DialogTitle>
            <DialogDescription>Asigna una clave a cada técnico/ayudante para que pueda recibir dinero de caja.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {technicians.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No hay técnicos activos</p>
            ) : technicians.map(t => (
              <div key={t.id} className="flex items-center justify-between border rounded-md p-2">
                <div>
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.tipo === 'ayudante' ? 'Ayudante' : 'Técnico'} {t.specialty ? `· ${t.specialty}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  {t.has_pin ? <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300"><Lock className="h-3 w-3 mr-1" />Tiene clave</Badge> : <Badge variant="outline" className="text-xs text-muted-foreground">Sin clave</Badge>}
                  <Button size="sm" variant="secondary" onClick={() => openTechPin(t)} data-testid={`set-tech-pin-${t.id}`}>{t.has_pin ? 'Cambiar' : 'Asignar'}</Button>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setTechPinDialogOpen(false)}>Cerrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set single Tech PIN sub-dialog */}
      <Dialog open={!!techPinTarget} onOpenChange={(v) => { if (!v) setTechPinTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Clave de {techPinTarget?.name}</DialogTitle>
            <DialogDescription>Esta clave se usará cuando este técnico/ayudante reciba dinero de caja.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSetTechPin} className="space-y-3">
            <div><Label className="text-xs">Nueva clave</Label><Input type="password" autoComplete="new-password" value={techPinForm.pin} onChange={(e) => setTechPinForm({...techPinForm, pin: e.target.value})} required minLength={4} /></div>
            <div><Label className="text-xs">Confirmar clave</Label><Input type="password" autoComplete="new-password" value={techPinForm.pin2} onChange={(e) => setTechPinForm({...techPinForm, pin2: e.target.value})} required minLength={4} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setTechPinTarget(null)}>Cancelar</Button><Button type="submit">Guardar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
